/**
 * NT-TOAST-GLOBAL-01: globaler Benachrichtigungs-Toast — erscheint in JEDEM Tab, nicht nur im
 * Auftraggeber-/Crime-Ops-Tab (Vorgänger: `showNpcReturnToast()` in crimeOps.ts, entfernt).
 *
 * Quelle sind die beiden bestehenden Notification-Systeme (N2 — kein neues Modell, kein Umbau):
 * - GameNotification  (/api/v1/notifications)         — liefert nur ungelesene.
 * - Admin-Notification (/api/v1/player-notifications) — liefert alle, mit isRead-Flag.
 *
 * Body-attached wie NotificationBell.ts/HamburgerMenu.ts, aus main.ts::initApp() gestartet
 * (Muster: services/npc-messages.ts) — läuft unabhängig davon, welche Seite/welcher Tab gerade
 * aktiv ist. Reine Eligibility-/Queue-/Erstladungs-Logik liegt in global-toast.logic.ts (isoliert
 * testbar, DOM-frei).
 */

import { api, ApiError } from './api.js';
import { gameState } from '../state/gameState.js';
import { navigate } from '../router.js';
import { calcNoticeDisplayMs, NOTICE_FADE_MS } from '../utils/notice-duration.js';
import {
  isToastEligible,
  decideToasts,
  mergeAndSortCandidates,
  pushToastQueue,
  toCandidateKey,
  buildReadEndpoint,
  type ToastCandidate,
  type ToastSource,
} from './global-toast.logic.js';

// TODO(balance): Playtesting-Startwerte (siehe NT-TOAST-GLOBAL-01, N4).
const TOAST_MAX_VISIBLE = 3;
// Spielereignisse (Razzia, Auftrag, Org …) bleiben mindestens so lange stehen, sonst nach Textlänge.
const TOAST_MIN_DISPLAY_MS = 12_000;
const POLL_INTERVAL_MS = 30_000;

const WRAP_ID = 'global-toast-wrap';

interface GameNotificationRow {
  id: string;
  type: string;
  title: string;
  text: string;
  actionUrl: string | null;
  read: boolean;
  createdAt: string;
}

interface AdminNotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  actionUrl: string | null;
  sentAt: string;
  isRead: boolean;
}

let initialized = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let requestInFlight = false;
let hasLoadedOnce = false;

/** Über die gesamte Session gewachsener Schutz gegen erneutes Toasten (N3, clientseitiger Teil). */
const toastedKeys = new Set<string>();
/** Aktuell sichtbare Toasts, älteste zuerst (N4 — max. TOAST_MAX_VISIBLE gleichzeitig). */
let displayQueue: string[] = [];
const elementsByKey = new Map<string, HTMLElement>();
const timersByKey = new Map<string, { fade: ReturnType<typeof setTimeout>; remove: ReturnType<typeof setTimeout> }>();

function ensureWrap(): HTMLElement {
  let wrap = document.getElementById(WRAP_ID);
  if (!wrap) {
    wrap = document.createElement('div');
    wrap.id = WRAP_ID;
    wrap.className = 'co-return-toast-wrap';
    document.body.appendChild(wrap);
  }
  return wrap;
}

function removeToastElement(key: string): void {
  const timers = timersByKey.get(key);
  if (timers) {
    clearTimeout(timers.fade);
    clearTimeout(timers.remove);
    timersByKey.delete(key);
  }
  elementsByKey.get(key)?.remove();
  elementsByKey.delete(key);
  displayQueue = displayQueue.filter((k) => k !== key);
}

function renderToastElement(candidate: ToastCandidate): void {
  const key = toCandidateKey(candidate);
  const wrap = ensureWrap();

  const toast = document.createElement('div');
  toast.className = 'co-return-toast';

  const text = document.createElement('div');
  text.textContent = candidate.text ? `${candidate.title} — ${candidate.text}` : candidate.title;
  toast.appendChild(text);

  const actions = document.createElement('div');
  actions.className = 'co-return-toast-actions';

  if (candidate.actionUrl) {
    const target = candidate.actionUrl;
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'co-return-toast-link';
    link.textContent = '→ ANSEHEN';
    link.addEventListener('click', () => {
      removeToastElement(key);
      navigate(target);
    });
    actions.appendChild(link);
  }

  toast.appendChild(actions);

  // N4: manuell schließbar (Muster: dashboard.ts .modal-close / home.ts .hn-lightbox-close).
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'co-return-toast-close';
  closeBtn.setAttribute('aria-label', 'Schließen');
  closeBtn.innerHTML = '&times;';
  closeBtn.addEventListener('click', () => { removeToastElement(key); });
  toast.appendChild(closeBtn);

  wrap.appendChild(toast);
  elementsByKey.set(key, toast);

  // Hover pausiert nur DIESEN Toast (nicht wrap-weit — mehrere Toasts können gleichzeitig stehen).
  toast.addEventListener('mouseenter', () => {
    const timers = timersByKey.get(key);
    if (!timers) return;
    clearTimeout(timers.fade);
    clearTimeout(timers.remove);
    toast.classList.remove('is-fading');
  }, { once: true });

  const removeMs = Math.max(TOAST_MIN_DISPLAY_MS, calcNoticeDisplayMs(toast.textContent));
  const fadeMs = removeMs - NOTICE_FADE_MS;
  const fade = setTimeout(() => { toast.classList.add('is-fading'); }, fadeMs);
  const remove = setTimeout(() => { removeToastElement(key); }, removeMs);
  timersByKey.set(key, { fade, remove });
}

/** N4: Anzeige auf max. TOAST_MAX_VISIBLE begrenzen — ältere rücken (sofort, nicht erst per Timer) raus. */
function applyQueue(incoming: ToastCandidate[]): void {
  const nextKeys = pushToastQueue(displayQueue, incoming.map(toCandidateKey), TOAST_MAX_VISIBLE);
  const bumped = displayQueue.filter((k) => !nextKeys.includes(k));
  for (const key of bumped) removeToastElement(key);
  displayQueue = displayQueue.filter((k) => nextKeys.includes(k));
  for (const candidate of incoming) renderToastElement(candidate);
}

function markRead(source: ToastSource, id: string): void {
  void api.post(buildReadEndpoint(source, id), {}).catch(() => undefined);
}

function toGameCandidates(rows: readonly GameNotificationRow[]): ToastCandidate[] {
  return rows
    .filter((r) => isToastEligible('GAME', r.type))
    .map((r) => ({
      id: r.id,
      source: 'GAME' as const,
      title: r.title,
      text: r.text,
      actionUrl: r.actionUrl,
      createdAt: r.createdAt,
    }));
}

function toAdminCandidates(rows: readonly AdminNotificationRow[]): ToastCandidate[] {
  return rows
    .filter((r) => !r.isRead && isToastEligible('ADMIN', r.type))
    .map((r) => ({
      id: r.id,
      source: 'ADMIN' as const,
      title: r.title,
      text: r.body,
      actionUrl: r.actionUrl,
      createdAt: r.sentAt,
    }));
}

async function checkForNotifications(): Promise<void> {
  if (!gameState.isAuthenticated() || requestInFlight) return;

  requestInFlight = true;
  try {
    const [gameResult, adminResult] = await Promise.allSettled([
      api.get<{ notifications: GameNotificationRow[] }>('/notifications'),
      api.get<AdminNotificationRow[]>('/player-notifications'),
    ]);

    const gameCandidates = gameResult.status === 'fulfilled' ? toGameCandidates(gameResult.value.notifications) : [];
    const adminCandidates = adminResult.status === 'fulfilled' ? toAdminCandidates(adminResult.value) : [];

    const merged = mergeAndSortCandidates(gameCandidates, adminCandidates);
    const isFirstLoad = !hasLoadedOnce;
    hasLoadedOnce = true;

    const { toShow, keysToMarkSeen } = decideToasts(merged, toastedKeys, isFirstLoad);
    for (const key of keysToMarkSeen) toastedKeys.add(key);
    for (const candidate of toShow) markRead(candidate.source, candidate.id);

    if (toShow.length > 0) applyQueue(toShow);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      stopPolling();
    }
  } finally {
    requestInFlight = false;
  }
}

function startPolling(): void {
  if (pollTimer !== null) return;

  void checkForNotifications();
  pollTimer = setInterval(() => { void checkForNotifications(); }, POLL_INTERVAL_MS);
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  hasLoadedOnce = false;
  for (const key of [...displayQueue]) removeToastElement(key);
}

export function initGlobalToast(): void {
  if (initialized) return;
  initialized = true;

  gameState.on('player:updated', () => { startPolling(); });
  gameState.on('auth:logout', () => { stopPolling(); });

  if (gameState.isAuthenticated()) startPolling();
}
